import * as THREE from 'three';
import { OrbitControls } from 'OrbitControls';
import { GLTFLoader } from 'GLTFLoader';
import { FontLoader } from 'FontLoader';
import { TextGeometry } from 'TextGeometry';

class ThreeDimensionalViewer {
    constructor(root) {
        this.root = root;

        this.view = null;
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.controls = null;
        this.animationId = null;

        this.meshes = {};
        this.nameMesh = null;
        this.topMesh = null;
        this.bottomMesh = null;
        this.logoMesh = null;

        this.fontCache = new Map();
        this.textureCache = new Map();
        this.floorY = 0;

        this.isVisible = false;
        this.isInitialized = false;
        this.isFullscreen = false;
        this.loader = this.root.querySelector('[data-loader]');
        this.controlsElement = this.root.querySelector('[data-controls]');
        this.placeholder = this.root.querySelector('[data-placeholder]');
        this.container = document.querySelector("[data-3d-model]");
        this.resizeTimeout = null;

        this.pendingUpdates = {
            fabric: null,
            text: null
        };

        this.needsRender = false;

        this.qualitySettings = this.getQualitySettings();
        this.frameCount = 0;
        this.lastFpsTime = performance.now();
        this.currentFps = 60;

        this.setupEventListeners();
        this.setupWebGLEventListeners();

        if (!this.isWebGLSupported()) {
            this.showWebGLFallback();
        } else if (this.root.dataset.instantShow === 'true') {
            this.show();
        }
    }

    isWebGLSupported() {
        if (window.location.search.includes('force-webgl-fallback')) {
            return false;
        }

        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        return !!gl;
    }

    getQualitySettings() {
        return {
            pixelRatio: Math.min(window.devicePixelRatio || 1, 2),
            shadowMapSize: 4096,
            shadowEnabled: true,
            antialias: true,
            curveSegments: 12,
            bevelSegments: 3,
            textureQuality: 1,
            maxDistance: 8,
            minDistance: 0.8
        };
    }

    async withLoader(callback) {
        this.showLoader();

        try {
            return await callback();
        } finally {
            this.hideLoader();
        }
    }

    showLoader() {
        if (this.loader && this.view) {
            this.loader.classList.remove('hidden');
            this.view.classList.remove('loaded');
        }
        if (this.placeholder && !this.isInitialized) {
            this.placeholder.classList.remove('hidden');
        }
    }

    hideLoader() {
        if (this.loader) {
            this.loader.classList.add('hidden');

            setTimeout(() => {
                if (this.view) {
                    this.view.classList.add('loaded');
                    this.requestRender();
                }
            }, 300);
        }
        if (this.placeholder && !this.isInitialized) {
            this.placeholder.classList.add('hidden');
        }
    }

    setupEventListeners() {
        document.addEventListener('viewToggleChanged', () => {
            if (!this.isVisible) {
                this.show();
            } else {
                this.hide();
            }
        });

        document.addEventListener('fabricTextureSelected', async event => {
            const detail = event.detail;

            if (detail && detail.textureSource) {
                this.pendingUpdates.fabric = detail;

                if (this.isVisible && this.isInitialized) {
                    await this.updateMaterial(detail.side, detail.textureSource);
                }

            }
        });

        document.addEventListener('patchOptionsChanged', async event => {
            const detail = event.detail || {};

            this.pendingUpdates.text = detail;

            if (this.isVisible && this.isInitialized) {
                await this.updateText(detail.color, detail.font, detail.petName);
            }
        });

        document.addEventListener('fullscreenchange', () => {
            if (!document.fullscreenElement) {
                this.root.classList.remove('fullscreen');
                this.updateFullscreenButton(false);
                this.resetView();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape' && this.root.classList.contains('fullscreen')) {
                this.exitFullscreen();
            }
        });

        if (!this.controlsElement) return;

        this.controlsElement.addEventListener('click', (event) => {
            const button = event.target.closest('[data-control]');
            if (!button) return;

            const control = button.dataset.control;
            this.handleControl(control);
        });
    }

    setupWebGLEventListeners() {
        window.addEventListener('beforeunload', () => {
            this.cleanupResources();
        });

        window.addEventListener('visibilitychange', () => {
            if (document.hidden && this.isVisible) {
                this.pauseRendering();
            } else if (!document.hidden && this.isVisible) {
                this.resumeRendering();
            }
        });
    }

    pauseRendering() {
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }

    resumeRendering() {
        if (this.isVisible && !this.animationId) {
            this.startAnimationLoopIfNeeded();
        }
    }

    handleContextLoss() {
        this.pauseRendering();
        this.isInitialized = false;
        this.showLoader();
    }

    async handleContextRestore() {
        await this.initialize();
        this.hideLoader();
    }

    showWebGLFallback() {
        this.root.classList.add('webgl-fallback-active');
        if (this.placeholder) {
            this.placeholder.classList.add('hidden');
        }
        if (this.loader) {
            this.loader.classList.add('hidden');
        }
    }

    handleControl(control) {
        switch (control) {
            case 'reset':
                this.resetView();
                break;
            case 'rotate':
                this.toggleAutoRotate();
                break;
            case 'zoom-in':
                this.zoomIn();
                break;
            case 'zoom-out':
                this.zoomOut();
                break;
            case 'fullscreen':
                this.toggleFullscreen();
                break;
        }
    }

    resetView() {
        if (!this.controls || !this.scene) return;

        this.updateRendererSize();
        this.frameCameraToObject(this.topMesh);
        this.requestRender();
    }

    toggleAutoRotate() {
        if (!this.controls) return;

        this.controls.autoRotate = !this.controls.autoRotate;
        this.updateRotateButton();

        if (this.controls.autoRotate) {
            this.startAnimationLoopIfNeeded();
        }

        this.requestRender();
    }

    updateRotateButton() {
        if (!this.controlsElement) return;

        const rotateButton = this.controlsElement.querySelector('[data-control="rotate"]');
        if (rotateButton) {
            if (this.controls.autoRotate) {
                rotateButton.classList.add('active');
            } else {
                rotateButton.classList.remove('active');
            }
        }
    }

    zoomIn() {
        if (!this.controls) return;

        const currentDistance = this.camera.position.distanceTo(this.controls.target);
        const newDistance = Math.max(currentDistance * 0.8, this.controls.minDistance);

        const direction = this.camera.position.clone().sub(this.controls.target).normalize();
        this.camera.position.copy(this.controls.target.clone().add(direction.multiplyScalar(newDistance)));
        this.controls.update();
        this.requestRender();
    }

    zoomOut() {
        if (!this.controls) return;

        const currentDistance = this.camera.position.distanceTo(this.controls.target);
        const newDistance = Math.min(currentDistance * 1.25, this.controls.maxDistance);

        const direction = this.camera.position.clone().sub(this.controls.target).normalize();
        this.camera.position.copy(this.controls.target.clone().add(direction.multiplyScalar(newDistance)));
        this.controls.update();
        this.requestRender();
    }

    toggleFullscreen() {
        if (!document.fullscreenElement) {
            this.enterFullscreen();
        } else {
            this.exitFullscreen();
        }
    }

    async enterFullscreen() {
        try {
            await this.root.requestFullscreen();
            this.root.classList.add('fullscreen');
            this.updateFullscreenButton(true);
            this.isFullscreen = true;
        } catch (error) {
            console.warn('Failed to enter fullscreen:', error);
        }
    }

    async exitFullscreen() {
        try {
            if (document.exitFullscreen) {
                await document.exitFullscreen();
            }
            this.root.classList.remove('fullscreen');
            this.updateFullscreenButton(false);
            this.isFullscreen = false;
        } catch (error) {
            console.warn('Failed to exit fullscreen:', error);
        }
    }

    updateFullscreenButton(isFullscreen) {
        if (!this.controlsElement) return;

        const fullscreenButton = this.controlsElement.querySelector('[data-control="fullscreen"]');
        if (fullscreenButton) {
            const enterIcon = fullscreenButton.querySelector('.dbc-controls__icon--fullscreen-enter');
            const exitIcon = fullscreenButton.querySelector('.dbc-controls__icon--fullscreen-exit');

            if (isFullscreen) {
                enterIcon.style.display = 'none';
                exitIcon.style.display = 'block';
            } else {
                enterIcon.style.display = 'block';
                exitIcon.style.display = 'none';
            }
        }
    }


    async show() {
        this.isVisible = true;

        if (!this.isWebGLSupported()) {
            this.showWebGLFallback();
            return;
        }

        if (!this.isInitialized) {
            await this.initialize();
        } else {
            this.startAnimationLoopIfNeeded();
            this.requestRender();
        }
    }

    hide() {
        this.isVisible = false;

        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }

        if (this.renderer) {
            this.renderer.dispose();
            this.renderer.forceContextLoss();
        }

        this.cleanupResources();
    }

    cleanupResources() {
        if (this.scene) {
            this.scene.traverse((object) => {
                if (object.geometry) {
                    object.geometry.dispose();
                }
                if (object.material) {
                    if (Array.isArray(object.material)) {
                        object.material.forEach(material => material.dispose());
                    } else {
                        object.material.dispose();
                    }
                }
            });
        }

        this.textureCache.forEach(texture => {
            if (texture && typeof texture.dispose === 'function') {
                texture.dispose();
            }
        });
        this.textureCache.clear();

        this.fontCache.clear();
    }

    async applyPendingUpdates() {
        if (this.pendingUpdates.fabric) {
            await this.updateMaterial(
                this.pendingUpdates.fabric.side,
                this.pendingUpdates.fabric.glbHandle,
                this.pendingUpdates.fabric.textureSource
            );
        }

        if (this.pendingUpdates.text) {
            await this.updateText(
                this.pendingUpdates.text.color,
                this.pendingUpdates.text.font,
                this.pendingUpdates.text.petName
            );
        }
    }

    async initialize() {
        if (this.isInitialized) {
            return;
        }

        if (!this.isWebGLSupported()) {
            this.showWebGLFallback();
            return;
        }

        this.buildCache();
        this.buildDOM();
        this.setupLighting();
        await this.loadModel();
        this.startAnimationLoopIfNeeded();

        this.isInitialized = true;
        await this.applyDefaultTextures();
        await this.applyPendingUpdates();
        this.updateRotateButton();
        this.requestRender();
    }

    async buildCache() {
        const topTexture = this.root.dataset.topTexture;
        const bottomTexture = this.root.dataset.bottomTexture;

        const texturePromises = [];

        if (topTexture && !this.textureCache.has(topTexture)) {
            texturePromises.push(this.loadTexture(topTexture));
        }

        if (bottomTexture && !this.textureCache.has(bottomTexture)) {
            texturePromises.push(this.loadTexture(bottomTexture));
        }

        if (texturePromises.length > 0) {
            try {
                await Promise.all(texturePromises);
            } catch (error) {
                console.warn('Failed to preload some textures:', error);
            }
        }
    }

    loadTexture(textureSource) {
        return new Promise((resolve, reject) => {
            if (!textureSource) {
                reject(new Error('Texture source is required'));
                return;
            }

            const textureCacheKey = textureSource.split('?')[0] || textureSource;
            if (this.textureCache.has(textureCacheKey)) {
                resolve(this.textureCache.get(textureCacheKey));
                return;
            }

            const textureLoader = new THREE.TextureLoader();

            textureLoader.load(
                textureSource,
                (texture) => {
                    if (!texture) {
                        reject(new Error('Failed to load texture'));
                        return;
                    }

                    texture.colorSpace = THREE.SRGBColorSpace;
                    texture.center.set(0.5, 0.5);
                    texture.rotation = Math.PI / 2;
                    texture.wrapS = texture.wrapT = THREE.RepeatWrapping;
                    texture.repeat.set(1, 1);

                    if (this.qualitySettings.textureQuality < 1) {
                        texture.generateMipmaps = false;
                        texture.minFilter = THREE.LinearFilter;
                        texture.magFilter = THREE.LinearFilter;
                    } else {
                        texture.generateMipmaps = true;
                        texture.minFilter = THREE.LinearMipmapLinearFilter;
                        texture.magFilter = THREE.LinearFilter;
                    }

                    this.textureCache.set(textureCacheKey, texture);
                    resolve(texture);
                },
                undefined,
                (error) => {
                    reject(error);
                }
            );
        });
    }

    buildDOM() {
        const container = document.createElement('div');
        container.className = 'dbc-view';
        container.style.width = '100%';
        container.style.height = '100%';

        this.root.style.position = 'relative';
        this.root.style.height = this.root.dataset.height || '100%';
        this.root.appendChild(container);

        const renderer = new THREE.WebGLRenderer({
            antialias: this.qualitySettings.antialias,
            alpha: true,
        });

        renderer.domElement.addEventListener('webglcontextlost', (event) => {
            event.preventDefault();
            this.handleContextLoss();
        });

        renderer.domElement.addEventListener('webglcontextrestored', () => {
            this.handleContextRestore();
        });

        renderer.setPixelRatio(this.qualitySettings.pixelRatio);

        const containerWidth = this.container.offsetWidth;
        const containerHeight = this.container.offsetHeight;

        if (containerWidth > 0 && containerHeight > 0) {
            renderer.setSize(containerWidth, containerHeight);
        }

        renderer.shadowMap.enabled = this.qualitySettings.shadowEnabled;
        renderer.shadowMap.type = this.qualitySettings.shadowEnabled ? THREE.PCFSoftShadowMap : THREE.BasicShadowMap;
        renderer.shadowMap.autoUpdate = this.qualitySettings.shadowEnabled;
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        container.appendChild(renderer.domElement);

        const scene = new THREE.Scene();

        const aspectRatio = container.clientWidth > 0 && container.clientHeight > 0
            ? container.clientWidth / container.clientHeight
            : 1;

        const camera = new THREE.PerspectiveCamera(
            35,
            aspectRatio,
            0.1,
            100
        );

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = this.root.dataset.enableDamping !== 'false';
        controls.dampingFactor = 0.07;
        controls.zoomToCursor = false;
        controls.zoomSpeed = this.parseNumber(this.root.dataset.zoom, 0.3);
        controls.autoRotate = this.root.dataset.autoRotate === 'true';
        controls.autoRotateSpeed = this.parseNumber(this.root.dataset.autoRotateSpeed, 2);
        controls.screenSpacePanning = false;
        controls.addEventListener('change', () => this.requestRender());

        new ResizeObserver(() => this.onResize()).observe(container);

        this.view = container;
        this.scene = scene;
        this.camera = camera;
        this.renderer = renderer;
        this.controls = controls;
    }

    setupFloor() {
        if (!this.qualitySettings.shadowEnabled) return;

        const geometry = new THREE.PlaneGeometry(50, 50);
        const material = new THREE.ShadowMaterial({
            opacity: 0.25
        });

        material.isUnique = true;
        const floor = new THREE.Mesh(geometry, material);
        floor.rotation.x = -Math.PI / 2;
        floor.position.y = this.floorY;
        floor.receiveShadow = true;
        floor.name = 'shadow-floor';
        this.scene.add(floor);
    }

    setupLighting() {
        if (!this.scene) return;

        const directionalLight = new THREE.DirectionalLight(0xffffff, 4);
        directionalLight.position.set(10, 45, 25);
        directionalLight.castShadow = this.qualitySettings.shadowEnabled;

        if (this.qualitySettings.shadowEnabled) {
            directionalLight.shadow.mapSize.width = this.qualitySettings.shadowMapSize;
            directionalLight.shadow.mapSize.height = this.qualitySettings.shadowMapSize;
            directionalLight.shadow.camera.near = 0.1;
            directionalLight.shadow.camera.far = 100;
            directionalLight.shadow.camera.left = -20;
            directionalLight.shadow.camera.right = 20;
            directionalLight.shadow.camera.top = 20;
            directionalLight.shadow.camera.bottom = -20;
        }

        this.scene.add(directionalLight);

        const ambientLight = new THREE.AmbientLight(0xffffff, this.qualitySettings.shadowEnabled ? 0.7 : 1.2);
        this.scene.add(ambientLight);
    }

    async loadModel() {
        const glbPath = this.root.dataset.glb;
        if (!glbPath) {
            throw new Error('GLB path is required');
        }

        const gltf = await this.loadGLTF(glbPath);
        if (!gltf || !gltf.scene) {
            throw new Error('Failed to load GLB model');
        }

        const sceneRoot = gltf.scene;

        sceneRoot.traverse(object => {
            if (object.isMesh) {
                this.meshes[object.name] = object;

                object.castShadow = this.qualitySettings.shadowEnabled;
                object.receiveShadow = false;

                if (object.name !== 'logo') {
                    object.material = new THREE.MeshLambertMaterial({ color: "#303030" });
                }

                object.material.isUnique = true;

                if (object.name === 'top') this.topMesh = object;
                else if (object.name === 'bottom') this.bottomMesh = object;
                else if (object.name === 'logo')  {
                    this.logoMesh = object;
                    const logoTexture = this.logoMesh.material.map;
                    this.logoMesh.material.dispose();
                    this.logoMesh.material = new THREE.MeshToonMaterial({
                        map: logoTexture,
                        transparent: true,
                        side: THREE.DoubleSide,
                        color: 0x000000,
                    });

                    this.logoMesh.castShadow = false;
                }
                else if (object.name === 'name') {
                    this.nameMesh = object;
                    this.nameMesh.material = new THREE.MeshPhongMaterial({ color: 0xffffff });
                    this.nameMesh.visible = false;
                } else object.visible = false;
            }
        });

        sceneRoot.rotation.y = THREE.MathUtils.degToRad(-90);
        this.scene.add(sceneRoot);

        const boundingBox = new THREE.Box3().setFromObject(sceneRoot);
        this.floorY = boundingBox.min.y;

        this.configureOrbitFloorLimit();
        this.frameCameraToObject(this.topMesh);

        if (this.nameMesh) {
            await this.initializeNameMesh();
        }

        this.setupFloor();
        this.hideLoader();
        if (this.placeholder) {
            this.placeholder.classList.add('hidden');
        }
        this.requestRender();
    }

    updateRendererSize() {
        if (!this.container || !this.renderer || !this.camera) return;

        const width = this.container.offsetWidth;
        const height = this.container.offsetHeight;

        if (width <= 0 || height <= 0) return;

        const maxHeight = this.root.dataset.height ? parseInt(this.root.dataset.height) : height;
        const finalHeight = (height > maxHeight) && !this.isFullscreen ? maxHeight : height;

        this.renderer.setSize(width, finalHeight);
        this.camera.aspect = width / finalHeight;
        this.camera.updateProjectionMatrix();

        this.controls.update();
        this.requestRender();
    }

    configureOrbitFloorLimit() {
        const epsilon = 0.01;
        this.controls.maxPolarAngle = Math.PI / 2 - epsilon;

        if (this.controls.target.y < this.floorY + epsilon) {
            this.controls.target.y = this.floorY + epsilon;
        }

        this.controls.addEventListener('change', () => {
            if (this.controls.target.y < this.floorY) {
                this.controls.target.y = this.floorY;
            }
        });
    }

    async initializeNameMesh() {
        this.nameMesh.userData.font = '';
        this.nameMesh.userData.text = '';
    }

    onResize() {
        if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
        }

        this.resizeTimeout = setTimeout(async () => {
            this.resetView();
        }, 200);
    }

    startAnimationLoopIfNeeded() {
        const needsLoop = this.controls.autoRotate || this.controls.enableDamping;
        if (!needsLoop || this.animationId) return;

        const loop = () => {
            if (!this.isVisible) {
                this.animationId = null;
                return;
            }

            this.controls.update();
            this.renderer.render(this.scene, this.camera);
            this.animationId = requestAnimationFrame(loop);
        };

        this.animationId = requestAnimationFrame(loop);
    }

    async applyDefaultTextures() {
        const topTexture = this.root.dataset.topTexture;
        const bottomTexture = this.root.dataset.bottomTexture;

        if (topTexture && this.topMesh) {
            await this.withLoader(() => this.applyTexture(this.topMesh, topTexture));
        }

        if (bottomTexture && this.bottomMesh) {
            await this.withLoader(() => this.applyTexture(this.bottomMesh, bottomTexture));
        }
    }

    requestRender() {
        if (!this.isVisible || !this.renderer || !this.scene || !this.camera) return;
        if (this.controls.autoRotate || this.controls.enableDamping) return;
        if (this.needsRender) return;
        this.needsRender = true;

        requestAnimationFrame(() => {
            this.needsRender = false;
            this.renderer.render(this.scene, this.camera);
        });
    }

    async updateText(color, fontName, petName) {
        if (!this.nameMesh) {
            return;
        }

        if (!color && !petName) {
            this.nameMesh.visible = false;
            this.logoMesh.material.color.set("#000000");
            this.logoMesh.material.emissive.set("#000000");
            this.requestRender();
            return;
        }

        const previousFont = this.nameMesh.userData.font;
        const previousText = this.nameMesh.userData.text;

        this.nameMesh.userData.font = fontName;
        this.nameMesh.userData.text = petName;

        if (color && this.nameMesh.material && this.nameMesh.material.color) {
            this.nameMesh.material.color.set(color);

            if (petName) {
                this.logoMesh.material.color.set(color);
                this.logoMesh.material.emissive.set(color);
            }

            this.requestRender();
        }

        const textGeometryNeedsReconstruction = this.nameMesh.userData.font !== previousFont || this.nameMesh.userData.text !== previousText;

        if (!textGeometryNeedsReconstruction) return;

        await this.withLoader(async () => {
            const font = await this.getFont(this.nameMesh.userData.font);
            this.recreateNameGeometry(font, this.nameMesh.userData.text);
        });
    }

    async updateMaterial(side, textureSource) {
        const nextMesh = this.meshes[side];

        if (!nextMesh) return;

        this.bottomMesh = this.bottomMesh.name === nextMesh.name ? this.bottomMesh : nextMesh;
        this.topMesh = this.topMesh.name === nextMesh.name ? this.topMesh : nextMesh;

        await this.withLoader(async () => {
            this.applyTexture(nextMesh, textureSource)
            await this.changeLogoColorToMatchMeshContract(side, textureSource);
        });

    }

    async applyTexture(mesh, textureSource) {
        const texture = await this.loadTexture(textureSource);
        this.applyCachedTexture(mesh, texture);
    }

    async changeLogoColorToMatchMeshContract(side, textureSource) {
        if (side !== 'top') return;

        const texture = await this.loadTexture(textureSource);

        if (!this.nameMesh.visible) {
            if (this.doesTextureHasDarkContract(texture)) {
                this.logoMesh.material.color.set("#ffffff");
                this.logoMesh.material.emissive.set("#ffffff");
            } else {
                this.logoMesh.material.color.set("#000000");
                this.logoMesh.material.emissive.set("#000000");
            }
        }
    }

    applyCachedTexture(mesh, texture) {
        if (mesh.material.map) {
            mesh.material.map.dispose();
        }

        mesh.material = new THREE.MeshLambertMaterial({ color: 0xffffff, map: texture });
        mesh.material.needsUpdate = true;

        this.requestRender();
    }

    recreateNameGeometry(font, text) {
        if (!font || !text || !this.nameMesh) {
            return;
        }

        if (this.nameMesh.geometry) {
            this.nameMesh.geometry.dispose();
        }

        const bevelSize = 0.005;
        const bevelThickness = 0.005;

        const geometryParameters = {
            font,
            size: 0.5 - (bevelSize + bevelThickness),
            depth: 0.02 - bevelThickness * 0.5,
            curveSegments: this.qualitySettings.curveSegments,
            bevelEnabled: true,
            bevelThickness: bevelThickness,
            bevelSize: bevelSize,
            bevelSegments: this.qualitySettings.bevelSegments,
        };

        const newGeometry = new TextGeometry(text, geometryParameters);
        newGeometry.center();

        const radius = 4;
        const position = newGeometry.attributes.position;
        const vector = new THREE.Vector3();

        for (let i = 0; i < position.count; i++) {
            vector.fromBufferAttribute(position, i);
            const angle = vector.x / radius;
            vector.x = Math.sin(angle) * radius;
            vector.z -= radius - Math.cos(angle) * radius;
            position.setXYZ(i, vector.x, vector.y, vector.z);
        }

        position.needsUpdate = true;
        newGeometry.computeVertexNormals();
        newGeometry.rotateX(-Math.PI / 2);
        newGeometry.translate(1.20, -0.1, 0.15);
        newGeometry.translate(-.25, 0, 0);

        this.nameMesh.geometry = newGeometry;
        this.nameMesh.visible = true;

        this.requestRender();
        this.frameCameraToObject(this.topMesh);
        this.stopRotation();
    }

    frameCameraToObject(object, offset = 1.5) {
        if (!this.camera || !this.controls || !object) return;

        const box = new THREE.Box3().setFromObject(object);

        if (box.isEmpty()) {
            return;
        }

        const size = box.getSize(new THREE.Vector3());
        const center = box.getCenter(new THREE.Vector3());

        const fovRadians = (this.camera.fov * Math.PI) / 180;
        const aspect = this.camera.aspect;

        const isMobile = window.innerWidth <= 750;
        const responsiveOffset = isMobile ? 1.8 : offset;

        const distanceX = Math.abs(size.x / 2 / Math.tan(fovRadians / 2)) * responsiveOffset;
        const distanceY = Math.abs(size.y / 2 / Math.tan(fovRadians / 2)) * responsiveOffset;

        let cameraZ = Math.max(distanceX, distanceY);

        if (aspect > 1) {
            cameraZ = Math.max(cameraZ, distanceX);
        } else {
            cameraZ = Math.max(cameraZ, distanceY);
        }

        const minDistance = Math.max(size.x, size.y, size.z) * this.qualitySettings.minDistance;

        if (cameraZ < minDistance) {
            cameraZ = minDistance;
        }

        const targetY = Math.max(center.y, this.floorY + 0.01);

        this.camera.position.set(center.x, targetY, center.z + cameraZ);
        this.camera.lookAt(center.x, targetY, center.z);

        this.controls.target.set(center.x, targetY, center.z);
        this.controls.minDistance = Math.max(size.x, size.y, size.z) * this.qualitySettings.minDistance;
        this.controls.maxDistance = Math.max(size.x, size.y, size.z) * this.qualitySettings.maxDistance;
        this.controls.update();
    }

    loadGLTF(path) {
        return new Promise((resolve, reject) => {
            if (!path) {
                reject(new Error('GLB path is required'));
                return;
            }

            const loader = new GLTFLoader();

            loader.load(
                path,
                (gltf) => {
                    if (!gltf) {
                        reject(new Error('Failed to load GLB file'));
                        return;
                    }
                    resolve(gltf);
                },
                undefined,
                (error) => {
                    reject(error);
                }
            );
        });
    }

    async getFont(name) {
        if (!name) {
            throw new Error('Font name is required');
        }

        if (this.fontCache.has(name)) {
            return this.fontCache.get(name);
        }

        const fontPromise = new Promise((resolve, reject) => {
            const loader = new FontLoader();

            const fontBaseUrl = (this.root.dataset.fontBaseUrl || '').split('?')[0] || null;

            if (fontBaseUrl) {
                const fontUrl = `${fontBaseUrl}${name}_typeface.json`;
                loader.load(
                    fontUrl,
                    (font) => {
                        if (!font) {
                            reject(new Error('Failed to load font'));
                            return;
                        }
                        resolve(font);
                    },
                    undefined,
                    (error) => reject(error)
                );
            } else {
                reject(new Error('Missing fontBaseUrl'));
            }
        });

        this.fontCache.set(name, fontPromise);

        return fontPromise;
    }

    parseNumber(value, fallback) {
        const number = parseFloat(value);

        if (Number.isFinite(number)) {
            return number;
        }

        return fallback;
    }

    stopRotation() {
        if (!this.controls || !this.controls.autoRotate) return;

        this.controls.autoRotate = false;
        if (this.autoRotateResumeTimer) {
            clearTimeout(this.autoRotateResumeTimer);
            this.autoRotateResumeTimer = null;
        }

        this.autoRotateResumeTimer = setTimeout(() => {
            if (this.isVisible && this.controls) {
                this.controls.autoRotate = true;
                this.startAnimationLoopIfNeeded();
                this.requestRender();
            }
            this.autoRotateResumeTimer = null;
        }, this.parseNumber(this.root.dataset.autoRotateResumeMs, 3000));
    }

    doesTextureHasDarkContract(texture) {
        try {
            const canvas = document.createElement('canvas');
            canvas.width = canvas.height = 1;
            const context = canvas.getContext('2d');
            context.drawImage(texture.image, 0, 0, 5, 5);

            const pixel = context.getImageData(0, 0, 1, 1).data;
            const brightness = (pixel[0] + pixel[1] + pixel[2]) / 3;

            return brightness < 128;
        } catch (error) {
            console.warn(error);
            return false;
        }
    }
}

document.querySelectorAll('[data-3d-model]').forEach(root => {
    new ThreeDimensionalViewer(root);
});